import React from 'react';
import * as PropTypes from 'prop-types';
import MuiAlert from '@material-ui/lab/Alert';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/styles';
import { ReactComponent as SuccessIcon } from './assets/success.svg';
import { ReactComponent as ErrorIcon } from './assets/error.svg';
import { ReactComponent as InfoIcon } from './assets/info.svg';
import { ReactComponent as WarningIcon } from './assets/warning.svg';
import { ReactComponent as WarningInfoIcon } from './assets/warningInfo.svg';
import { ReactComponent as SettingIcon } from './assets/setting.svg';
import { ReactComponent as MessageIcon } from './assets/message.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { colors } from '../../../style/colors';
import { ITheme } from '../../../style/theme';

const getColorByType = (severity: string, colors: any) => {
  return (
    (severity === 'success' && colors.darkGreen) ||
    (severity === 'info' && colors.darkBlue) ||
    (severity === 'warning' && colors.darkOrange) ||
    (severity === 'warningInfo' && colors.darkOrange) ||
    (severity === 'error' && colors.darkRed) ||
    (severity === 'setting' && colors.darkBlue) ||
    (severity === 'message' && colors.darkBlue)
  );
};

const getBgColorByType = (severity: string, colors: any) => {
  return (
    (severity === 'success' && colors.lightGreen) ||
    (severity === 'info' && colors.skyBlue) ||
    (severity === 'warning' && colors.lightOrange) ||
    (severity === 'warningInfo' && colors.lightOrange) ||
    (severity === 'error' && colors.lightRed) ||
    (severity === 'setting' && colors.skyBlue) ||
    (severity === 'message' && colors.skyBlue)
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  root: (props) => ({
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    alignItems: 'center',
    backgroundColor: getBgColorByType(props.severity, colors),
    boxShadow:
      '0px 3px 5px rgba(0, 0, 0, 0.2), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12)',
    borderRadius: 32,
    border: 'none',
    [theme.breakpoints.up('xs')]: {
      width: 321,
    },
    [theme.breakpoints.up('sm')]: {
      width: 516,
    },
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      minWidth: 600,
      maxWidth: 710,
    },
  }),
  icon: {
    padding: theme.spacing(1, 0),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 0),
    },
  },
  message: (props) => ({
    fontSize: 16,
    lineHeight: '18px',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 0),
      lineHeight: '24px',
    },
    padding: theme.spacing(1, 0),
    color: getColorByType(props.severity, colors),
  }),
  action: {
    paddingRight: theme.spacing(1),
    '& path': {
      fill: (props: { severity: any }) =>
        getColorByType(props.severity, colors),
    },
  },
}));

const Alert = (props: any) => {
  const { severity, onClose, ...rest } = props;

  const classes = useStyles({ severity });

  return (
    <MuiAlert
      classes={classes}
      severity={severity}
      iconMapping={{
        success: <SuccessIcon />,
        error: <ErrorIcon />,
        info: <InfoIcon />,
        warning: <WarningIcon />,
        warningInfo: <WarningInfoIcon />,
        setting: <SettingIcon />,
        message: <MessageIcon />,
      }}
      action={
        onClose ? (
          <IconButton color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        ) : null
      }
      {...rest}
    />
  );
};

Alert.propTypes = {
  severity: PropTypes.oneOf([
    'success',
    'error',
    'info',
    'warning',
    'setting',
    'message',
  ]),
  onClose: PropTypes.func,
};

Alert.defaultProps = {
  severity: 'success',
  onClose: null,
};

export default Alert;
